// ユーザー詳細.
import React, { FC, useState, useEffect, useRef, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import _ from 'lodash';
import { RouteComponentProps } from 'react-router-dom';

import store from '../store/configureStore';

import * as moffAPIActions from '../actions/MoffAPI';
import * as HeaderActions from '../actions/Header';
import * as pdfCreateActions from '../actions/pdfCreate';

import * as Header from '../constants/Header';
import * as UserListField from '../constants/UserListField';

import { objectQuickSort, duplicateRepeatSort, dispatchIdentifierToGTM } from '../utils/commonUtil';

import PopupContainer from './Popup';

import PageLoader from '../components/Atoms/PageLoader';
import KirokuAllUsersPrint from '../components/Organisms/KirokuAllUsersPrint';
import { RefFunction } from '../components/Molecules/UserListShow';

type AllState = ReturnType<typeof store.getState>;

const KirokuAllUsersPrintContainer: FC<RouteComponentProps> = ({ location }) => {
  const dispatch = useDispatch();
  const moffAPIGlobalState = useSelector((state: AllState) => state.moffAPI);
  const pdfCreateGlobalState = useSelector((state: AllState) => state.pdfCreate);

  const [date, setDate] = useState(moment());
  const [, setDateYYYYMM] = useState(moment().format('YYYY-MM'));
  const [cachedUsers, setCachedUsers] = useState<UserListField.User[]>([] as UserListField.User[]);

  const refUsersList = useRef<RefFunction | null>(null);

  // 最初に一回ユーザー一覧取得のために通信.
  useEffect(() => {
    (async () => {
      document.title = '個別機能訓練一括PDF出力';
      dispatch(HeaderActions.setPage(Header.KIROKU_ALL_USER_PRINT));
      await dispatch(moffAPIActions.getUsers());
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 個別機能訓練一括出力利用者リスト用データ取得後、利用者のデータをローカルステートにキャッシュさせておく
  useEffect(() => {
    // キャッシュさせておく.
    if (_.isEmpty(cachedUsers) && !_.isEmpty(moffAPIGlobalState.result ?? [])) {
      setCachedUsers(
        duplicateRepeatSort(UserListField.USERS_SORT_KEYS, moffAPIGlobalState.result ?? [], objectQuickSort),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moffAPIGlobalState.Loaded]);

  // 月選択画面 矢印のアクション
  const minusMonthly = () => {
    const lastMonth = date.subtract(1, 'months');
    setDate(lastMonth);
    setDateYYYYMM(lastMonth.format('YYYY-MM'));
    dispatchIdentifierToGTM('all_users_change_month_kiroku');
  };

  const plusMonthly = () => {
    const nextMonth = date.add(1, 'months');
    setDate(nextMonth);
    setDateYYYYMM(nextMonth.format('YYYY-MM'));
    dispatchIdentifierToGTM('all_users_change_month_kiroku');
  };

  // トレーニング記録取得のAPIを実行
  const runAPIKirokuReport = async () => {
    // ローディングされている最中はボタンを押しても、APIが送信されない
    if (moffAPIGlobalState.Loading) {
      return;
    }
    // チェックボックスがonのユーザーのみを取得
    const checkedUsers: UserListField.User[] = refUsersList.current?.getCheckedUsers() ?? [];
    if (checkedUsers.length === 0) {
      alert('ユーザーが選択されていません');
      return;
    }

    const userIds = _.map(checkedUsers, (user) => user.user_id);
    const selectMonth = date.format('YYYY-MM');

    await dispatch(pdfCreateActions.createKirokuPDF(selectMonth, userIds));
    dispatchIdentifierToGTM('output_all_users_report_kiroku');
  };

  /**
   * CSSの描画.
   */
  const RenderCSS: FC = () => {
    return (
      <style type="text/css">
        {/* 縦印刷, 日付・title名ヘッダの余白 */}
        {'@media print{@page {size: portrait; margin-top: 43px;}}'}
        {'@media print{#wrapper { padding:0; margin: 0; } header { display: none; }}'}
        {'@media print{.contentIn { padding-top:0!important; padding-bottom:0!important; }}'}
        {'@media print{.report { padding-bottom:0!important; }}'}
        {/* Edge対策 */}
        {'@media print{tr { page-break-inside: avoid; }}'}
      </style>
    );
  };

  return (
    <Fragment>
      <RenderCSS />
      <PopupContainer />
      {(!moffAPIGlobalState.Loaded && !cachedUsers) || pdfCreateGlobalState.Loading ? <PageLoader /> : null}
      <KirokuAllUsersPrint
        location={location}
        refUsersList={refUsersList}
        minusMonthly={minusMonthly}
        plusMonthly={plusMonthly}
        allUsersPrintButtonClickHandler={runAPIKirokuReport}
        date={date}
        loaded={moffAPIGlobalState.Loaded}
        cachedUsers={cachedUsers}
      />
    </Fragment>
  );
};

export default KirokuAllUsersPrintContainer;
